import intersection from 'lodash.intersection';
import cloneDeep from 'lodash.clonedeep';
import Immutable from 'seamless-immutable';

import DIVISOR from '../constants/divisor';
import documentTemplate, { sizeTemplate } from '../constants/document-template';
import { getDefaultGroups, getPossibleGroups, getTargets } from '../constants/targets';
import { hasPromo } from './cropsUtil';
import getFilter from './getFilter';
import mustRender from './mustRender';
import possibleCharts from './possibleCharts';
import { colorsForProduct } from '../constants/colors';

/**
 * @returns a valid chart document.
 */
function validChartDocument(doc, preserveSavedState = true, writeMustRender = true) {
  const validDoc = cloneDeep(documentTemplate);
  const vc = validDoc.chart;

  let dc = doc ? doc.chart : null;

  if (!dc) {
    doc = { chart: {} };
    dc = doc.chart;
  }

  dc.data = dc.data || {};
  dc.admin = dc.admin || {};
  validDoc.id = doc.id || doc._id || validDoc.id;
  validDoc.documentId = doc.documentId || validDoc.documentId;
  validDoc.svg = pushSvgIntoSizes(doc);
  validDoc.svgAi = {};
  validDoc.svgBmp = {};
  validDoc.status = doc.status || validDoc.status;
  validDoc.history = doc.history || validDoc.history;
  validDoc.urls = doc.urls || validDoc.urls;
  validDoc.selectedPoint = doc.selectedPoint || validDoc.selectedPoint;
  vc.title = dc.title || vc.title;
  vc.type = dc.type || vc.type;
  vc.step = dc.step || vc.step;
  vc.manualWidth = dc.manualWidth || vc.manualWidth;
  vc.manualHeight = dc.manualHeight || vc.manualHeight;
  vc.markdown = dc.markdown || vc.markdown;
  vc.publishedImageRatio = dc.publishedImageRatio || vc.publishedImageRatio;
  vc.data.marketTime = dc.data.marketTime;

  vc.color = dc.color || vc.color;

  vc.data.raw = dc.raw_data || dc.data.raw || vc.data.raw;
  vc.data.parsed = dc.data.parsed || vc.data.parsed || null;
  vc.data.isScatterplot = dc.isScatterplot || dc.data.isScatterplot || vc.data.isScatterplot;
  vc.data.suppress = dc.data.suppress || vc.data.suppress;

  vc.data.horizontalBarType =
    dc.horizontalBarType || dc.data.horizontalBarType || vc.data.horizontalBarType;

  vc.data.specialHorizontalBarXValue =
    dc.specialHorizontalBarXValue ||
    dc.data.specialHorizontalBarXValue ||
    vc.data.specialHorizontalBarXValue;
  if (dc.data.horizontalBarLabel === undefined) {
    dc.data.horizontalBarLabel = true;
  }
  vc.data.horizontalBarLabel = !!dc.data.horizontalBarLabel;

  vc.data.series = dc.series || dc.data.series || vc.data.series;
  vc.data.categories = dc.data.categories || vc.data.categories;
  vc.data.dateSpan = dc.data.dateSpan || vc.data.dateSpan;
  vc.data.isIntraday = dc.data.isIntraday !== undefined ? dc.data.isIntraday : vc.data.isIntraday;
  vc.data.needsCommas =
    dc.data.needsCommas !== undefined ? dc.data.needsCommas : vc.data.needsCommas;
  vc.data.type = dc.data.type || vc.data.type;
  vc.data.tickers = dc.data.tickers || vc.data.tickers;
  vc.data.frequency = dc.data.frequency || vc.data.frequency;
  vc.data.start = dc.data.start || vc.data.start;
  vc.data.end = dc.data.end || vc.data.end;
  vc.data.dateRange = dc.data.dateRange || vc.data.dateRange;
  vc.data.companies = dc.data.companies || vc.data.companies;
  vc.data.isPercent = dc.data.isPercent !== undefined ? dc.data.isPercent : vc.data.isPercent;
  vc.data.singleIsPercent =
    dc.data.singleIsPercent !== undefined ? dc.data.singleIsPercent : vc.data.singleIsPercent;
  vc.data.isRolling = dc.data.isRolling !== undefined ? dc.data.isRolling : vc.data.isRolling;
  vc.data.disableShowAsPercent =
    dc.data.disableShowAsPercent !== undefined
      ? dc.data.disableShowAsPercent
      : vc.data.disableShowAsPercent;

  // Look up new possible chart types based on the saved data, otherwise new
  // chart types will not be available until the raw data is reprocessed.
  vc.data.possibleCharts = possibleCharts(vc.data);
  //dc.data.possibleCharts || vc.data.possibleCharts;

  vc.targets = validateTargets(dc.targets || vc.targets);
  vc.groups = validateGroups(vc.targets, dc.groups, vc.groups);
  vc.product = validateProduct(vc.targets, dc.product || vc.product);
  vc.print = dc.print || vc.print;
  if (dc.showCharts === undefined) {
    dc.showCharts = true;
  }
  vc.showCharts = !!dc.showCharts;
  if (dc.data.showMinorTicks === undefined) {
    dc.data.showMinorTicks = true;
  }
  vc.data.showMinorTicks = !!dc.data.showMinorTicks;
  if (dc.data.showTooltips === undefined) {
    dc.data.showTooltips = true;
  }
  vc.data.showTooltips = !!dc.data.showTooltips;
  vc.data.connectEmptyDataPoints = dc.data.connectEmptyDataPoints || vc.data.connectEmptyDataPoints;

  if (dc.data.markedDataPoints === undefined) dc.data.markedDataPoints = false;
  vc.data.markedDataPoints = !!dc.data.markedDataPoints;

  vc.sizes = pushDataIntoSizes(dc, vc.targets);
  vc.notes = pushNotesIntoSizes(dc, vc.targets);

  vc.admin.sections = dc.sections || dc.admin.sections || vc.admin.sections;
  if (dc.admin.hasOwnProperty('notes')) {
    vc.admin.notes = dc.admin.notes;
  } else if (dc.hasOwnProperty('notes')) {
    vc.admin.notes = dc.notes;
  }
  vc.admin.approved = dc.approved || dc.admin.approved || vc.admin.approved;

  if (preserveSavedState !== true) {
    // never called because preserveSavedState is always true
    validDoc.saved = cloneDeep(validDoc.chart);
    validDoc.dirty = false;
  }
  validDoc.mustRender = writeMustRender !== false ? mustRender(validDoc) : dc.mustRender || [];

  validDoc.approvedEditor = doc.approvedEditor || null;
  validDoc.approvedGraphics = doc.approvedGraphics || null;
  validDoc.createDate = doc.createDate || null;
  validDoc.creator = doc.creator || null;
  validDoc.modifier = doc.modifier || null;
  validDoc.modifyDate = doc.modifyDate || doc.createDate || null;
  if (validDoc.chart?.product !== 'wsj' && validDoc.chart?.data?.series)
    validateColors(validDoc.chart.data.series, validDoc.chart.product);
  return Immutable(validDoc);
}

// to handle situations for non-wsj pubs when series color is a wsj color due to combined publications logic we had previously
function validateColors(series, product) {
  const pallete = colorsForProduct(product);
  series.forEach((individualSeries, i) => {
    if (!pallete.includes(individualSeries.color)) {
      individualSeries.color = pallete[individualSeries.initialIndex] || pallete[i];
    }
  });
}

function validateTargets(targets) {
  const TARGETS = getTargets();
  if (targets.length === 0) {
    targets = getFilter().targets.slice(0);
  }
  for (let i = 0; i < targets.length; i++) {
    const target = targets[i].indexOf('wsj') === 0 ? 'wsj' : targets[i];
    for (let j = 0; j < TARGETS.length; j++) {
      if (target === TARGETS[j].value) {
        return [target];
      }
    }
  }
  return [];
}

function validateGroups(targets, testGroups) {
  // Because "wsj" used to be "wsjArticle" and "wsjSectionfront":
  const groups = [];
  if (targets.indexOf('wsjArticle') !== -1) {
    groups.push('Article');
  }
  if (targets.indexOf('wsjSectionFront') !== -1) {
    groups.push('Homepage');
  }
  if (groups.length) {
    return groups;
  }
  // New "wsj" and all DJMG:
  const target = targets[0];
  if (target) {
    if (Array.isArray(testGroups)) {
      const possibleGroups = getPossibleGroups(target);
      const savedGroups = target === 'wsj' ? convertGroups(testGroups) : testGroups;
      return intersection(savedGroups, possibleGroups);
    }
    return getDefaultGroups(target);
  }
  return [];
}

/*
 * For WSJ charts made before group names changed,
 * ensure that proper groups are shown
 * by converting old name to corresponding new name
 */
function convertGroups(groups) {
  return groups.map((group) => {
    switch (group) {
      case 'Dynamic Insets':
        return 'Article';
      case 'Large Insets':
        return 'Article (wide)';
      case 'Section Front Images':
        return 'Homepage';
      case 'Mobile Images':
        return 'App';
      default:
        return group;
    }
  });
}

function validateProduct(targets, product) {
  if (product === 'wsj') return product;
  return targets[0] !== product ? targets[0] : product;
}

function pushSvgIntoSizes(doc) {
  let svg = {};
  const TARGETS = getTargets();

  TARGETS.forEach((target) => {
    target.sizes.forEach((size) => {
      let id = size.id;
      if (doc.svg && doc.svg[id]) {
        svg[id] = doc.svg[id];
      } else {
        svg[id] = null;
      }
    });
  });
  return svg;
}

function pushNotesIntoSizes(doc) {
  let notes = {};
  const TARGETS = getTargets();

  TARGETS.forEach((target) => {
    target.sizes.forEach((size) => {
      let id = size.id;
      if (doc.notes && doc.notes[id]) {
        notes[id] = doc.notes[id];
      } else {
        notes[id] = null;
      }
    });
  });
  return notes;
}

function pushDataIntoSizes(dc, targets) {
  let sizes = {};
  let tmpl = cloneDeep(sizeTemplate);
  const TARGETS = getTargets();

  TARGETS.forEach((target) => {
    target.sizes.forEach((size) => {
      let id = size.id;
      sizes[id] = {};
      if (dc.sizes && dc.sizes[id]) {
        let dsize = dc.sizes[id];
        sizes[id].axes = dsize.axes || tmpl.axes;
        sizes[id].xAxis = dsize.xAxis || tmpl.xAxis;
        sizes[id].yAxis = dsize.yAxis || tmpl.yAxis;
        sizes[id].stacking = dsize.stacking || tmpl.stacking;
        sizes[id].tooltips = dsize.tooltips || tmpl.tooltips;
        sizes[id].height = dsize.hasOwnProperty('height') ? dsize.height : tmpl.height;
        sizes[id].editing = dsize.hasOwnProperty('editing') ? dsize.editing : tmpl.editing;
        if (dsize.hasOwnProperty('width')) {
          sizes[id].width = dsize.width;
        }
        if (dsize.hasOwnProperty('lines')) {
          sizes[id].lines = dsize.lines;
        }
        if (dsize.hasOwnProperty('columns')) {
          sizes[id].columns = dsize.columns;
        }
      } else {
        const isPromo = hasPromo(id);
        if (isPromo && dc.sizes) {
          sizes[id].axes = cloneDeep(dc.sizes['4u'].axes);
          sizes[id].xAxis = cloneDeep(dc.sizes['4u'].xAxis);
          sizes[id].yAxis = cloneDeep(dc.sizes['4u'].yAxis);
          sizes[id].stacking = dc.sizes['4u'].stacking;
          sizes[id].tooltips = cloneDeep(dc.sizes['4u'].tooltips);
        } else {
          sizes[id].axes = dc.axes || tmpl.axes;
          sizes[id].xAxis = dc.xAxis || tmpl.xAxis;
          sizes[id].yAxis = dc.yAxis || tmpl.yAxis;
          sizes[id].stacking = dc.stacking || tmpl.stacking;
          sizes[id].tooltips = dc.tooltips || tmpl.tooltips;
        }
        sizes[id].height = dc.hasOwnProperty('height') ? dc.height : tmpl.height;
        sizes[id].editing = dc.hasOwnProperty('editing') ? dc.editing : tmpl.editing;
        if (dc.hasOwnProperty('lines')) {
          sizes[id].lines = dc.lines;
        }
        if (dc.hasOwnProperty('columns')) {
          sizes[id].columns = dc.columns;
        }
      }

      sizes[id].tooltips = fixUnits(sizes[id].tooltips, true);
      sizes[id].axes.units = fixUnits(sizes[id].axes.units);

      if (targets.indexOf(target.value) === -1) {
        sizes[id].editing = true;
      }

      if (id.indexOf('print') === 0) {
        sizes[id].lines = sizes[id].lines || size.lines;
        sizes[id].height = sizes[id].height || size.height;
        sizes[id].columns = sizes[id].columns || size.columns;
        sizes[id].width = sizes[id].width || size.width;
      } else {
        if (!size.hasOwnProperty('maxHeight') && sizes[id].height !== size.height) {
          sizes[id].height = size.height;
        } else if (size.hasOwnProperty('minHeight') && size.hasOwnProperty('maxHeight')) {
          sizes[id].height = Math.max(
            size.minHeight,
            Math.min(size.maxHeight, sizes[id].height || size.height)
          );
        }
        sizes[id].height = sizes[id].height === 0 ? size.height : sizes[id].height;
      }

      if (!sizes[id].axes.units || typeof sizes[id].axes.units === 'string') {
        sizes[id].axes.units = {
          suffix: sizes[id].axes.units || '',
        };
      }
      sizes[id].axes = Object.assign({}, tmpl.axes, sizes[id].axes);
      sizes[id].axes.units = Object.assign({}, tmpl.axes.units, sizes[id].axes.units);
      sizes[id].tooltips.sameAsAxes =
        sizes[id].tooltips.sameAsAxes === true || sizes[id].tooltips.sameAsAxes === false
          ? sizes[id].tooltips.sameAsAxes
          : true;
    });
  });
  return sizes;
}

function fixUnits(unitsA = {}, isTooltips = false) {
  const unitsB = {
    commas: !!unitsA.commas,
    prefix: unitsA.prefix || '',
    suffix: unitsA.suffix || '',
    divisor: unitsA.divisor || DIVISOR[0],
    forceDecimals: unitsA.forceDecimals ? unitsA.decimals : undefined,
    maxDecimals: unitsA.hasOwnProperty('maxDecimals') ? unitsA.maxDecimals : undefined,
  };
  if (unitsA.hasOwnProperty('forceDecimals')) {
    unitsB.forceDecimals = unitsA.forceDecimals;
  } else if (unitsA.hasOwnProperty('formatDecimals')) {
    unitsB.forceDecimals = unitsA.formatDecimals ? unitsA.decimals : undefined;
  } else {
    unitsB.forceDecimals = undefined;
  }
  if (isTooltips) {
    unitsB.sameAsAxes =
      unitsA.sameAsAxes === true || unitsA.sameAsAxes === false ? unitsA.sameAsAxes : true;
  }
  return unitsB;
}

function validateUrls(urls) {
  let keys = Object.keys(urls);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (urls[key] && typeof urls[key] === 'object') {
      urls[key] = 'Unknown Error';
    }
    if (key === 'iframe' && typeof urls.iframe === 'string') {
      urls.iframe = urls.iframe.replace(/^http:/, 'https:');
    }
  }
  return urls;
}

export default validChartDocument;
